import React from 'react';
import { setReturn } from '../operations/ReturnToList.jsx';
import ActivitiesAndDeliveries from './ActivitiesAndDeliveries.jsx';
const { useState, useEffect } = React;
import {
  COACHING_MODALITIES, COACHING_FOCUS, COACHING_SPECIALTIES, PROCESS_STATUSES, STARTER_NOTE, SCOPE_NOTE, WEEKDAYS,
  TRANSCRIPT_DEFAULTS, label,
} from '../../coachingConfig.js';
import { fmtInTz } from '../../coachingTime.js';
import { inputSt, textareaSt, labelSt, cardSt, mutedSt, badgeSt, warnSt, Field, SubTabs } from '../diagnostic/shared.jsx';
import { coachCall, coachingPeopleApi, Visibility, MoreOptions, Row, Empty, useCoachingTools } from './shared.jsx';
import PersonPicker from '../people/PersonPicker.jsx';
import { adminCall } from '../diagnostic/shared.jsx';
import { ROLES, DIAGNOSTIC_STATUSES, t } from '../../diagnosticConfig.js';
import ProgramEditor from './ProgramEditor.jsx';
import TimeZoneSelect from '../ui/TimeZoneSelect.jsx';
import { ContactPicker, PersonContact } from './ContactLink.jsx';
import { PageHeader, LoadingState, ErrorState } from '../ui/States.jsx';
import NotificationCenter from '../ui/NotificationCenter.jsx';
import { timeZoneOption } from '../../timeZones.js';
import LearningTab from './LearningTab.jsx';
import ProcessDetail from './ProcessDetail.jsx';
import PersonProfile from './PersonProfile.jsx';
import DuplicateReview from './DuplicateReview.jsx';

// "Acompañamiento": the coach's space. Programs (editable templates), people,
// processes (individual or team, each with its own copy of the program), the
// agenda (availability for bookings) and the messages. Talks only to
// /api/coaching-admin, which checks that the user is the coach.

function StatusBanner({ status }) {
  if (!status) return null;
  const pending = status.integrations.filter(i => !i.connected);
  return (
    <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
      {status.environment === 'test' && (
        <div style={{ ...warnSt, marginBottom: 0, fontSize: 13 }}>🧪 Entorno de prueba: los datos se guardan en la base de prueba ({status.base}), no en producción. Gmail y los servicios pagos están desactivados.</div>
      )}
      {status.schema === false && (
        <div style={{ ...warnSt, marginBottom: 0, fontSize: 13 }}>
          ⚠️ Faltan las tablas de Acompañamiento en Airtable. Corre <code>scripts/coaching-migration.mjs</code> (ver docs/coaching/configuracion.md). Mientras tanto no se puede guardar nada.
        </div>
      )}
      {!status.ai && <div style={{ ...mutedSt }}>La IA no está configurada en este entorno: las propuestas de sesión no van a funcionar.</div>}
      {pending.length > 0 && (
        <div style={{ ...mutedSt }}>
          Conexiones externas pendientes: {pending.map(i => `${i.label}${i.configured ? ' (variables cargadas, conexión todavía no implementada)' : ' (sin configurar)'}`).join(' · ')}.
          Los turnos y mensajes funcionan dentro de Oike; los avisos y el calendario externo no se envían.
        </div>
      )}
    </div>
  );
}

// ── Programs ──

const NEW_PROGRAM = { name: '', description: '', modality: 'individual', focus: 'personal', specialties: [], durationWeeks: 8, sessionMinutes: 60, content: { goals: [], sequence: [{ n: 1, kind: 'individual', title: 'Encuadre y acuerdo', purpose: '', tools: [], between: [] }], activities: [], tools: [], review: '', closing: '' } };

function ProgramsTab({ programs, reload }) {
  const [editing, setEditing] = useState(null); // { id?, version?, program }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const tools = useCoachingTools();
  const run = async (fn) => { setBusy(true); setError(''); try { await fn(); } catch (e) { setError(e.message); } setBusy(false); };
  const open = (id) => run(async () => { const { program } = await coachCall({ action: 'getProgram', id }); setEditing({ id, version: program.version, program, status: program.status, starterKey: program.starterKey }); });
  const save = () => run(async () => { await coachCall({ action: 'saveProgram', id: editing.id, baseVersion: editing.version, program: editing.program }); setEditing(null); reload(); });

  if (editing) return (
    <div style={cardSt}>
      <Row style={{ justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>{editing.id ? 'Editar programa' : 'Nuevo programa'}</h3>
        {editing.starterKey && <span style={badgeSt(false)}>Propuesta editable</span>}
      </Row>
      {editing.starterKey && <p style={mutedSt}>{STARTER_NOTE}</p>}
      {editing.id && <p style={mutedSt}>Los cambios no alteran los procesos ya iniciados: cada proceso tiene su propia copia.</p>}
      {error && <div style={{ ...warnSt, padding: 10 }}>{error}</div>}
      <ProgramEditor value={editing.program} onChange={p => setEditing({ ...editing, program: p })} tools={tools} />
      <Row style={{ marginTop: 12 }}>
        <button className="action-btn btn-primary" disabled={busy} onClick={save}>Guardar</button>
        <button className="action-btn" disabled={busy} onClick={() => setEditing(null)}>Cancelar</button>
        {editing.id && editing.status !== 'active' && <button className="action-btn" disabled={busy} onClick={() => run(async () => { await coachCall({ action: 'setProgramStatus', id: editing.id, status: 'active' }); setEditing(null); reload(); })}>Activar</button>}
        {editing.id && editing.status !== 'archived' && <button className="action-btn" disabled={busy} onClick={() => run(async () => { await coachCall({ action: 'setProgramStatus', id: editing.id, status: 'archived' }); setEditing(null); reload(); })}>Archivar</button>}
      </Row>
    </div>
  );

  return (
    <div style={cardSt}>
      <Row style={{ justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Programas</h3>
        <Row>
          <button className="action-btn" disabled={busy} onClick={() => run(async () => { const r = await coachCall({ action: 'seedStarterPrograms' }); setError(r.created ? '' : 'Las propuestas iniciales ya estaban creadas.'); reload(); })}>Cargar propuestas iniciales</button>
          <button className="action-btn btn-primary" onClick={() => setEditing({ program: NEW_PROGRAM })}>+ Nuevo programa</button>
        </Row>
      </Row>
      {error && <div style={{ ...warnSt, padding: 10 }}>{error}</div>}
      {!programs.length ? <Empty>Todavía no hay programas. Carga las propuestas iniciales o crea uno.</Empty> : (
        <div style={{ display: 'grid', gap: 8 }}>
          {programs.map(p => (
            <button key={p.id} onClick={() => open(p.id)} style={{ textAlign: 'left', background: 'rgba(30,42,44,0.03)', border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10, color: 'var(--globant-text)', cursor: 'pointer' }}>
              <Row>
                <b style={{ fontSize: 13 }}>{p.name}</b>
                <span style={badgeSt(p.status === 'active')}>{{ draft: 'Borrador', active: 'Activo', archived: 'Archivado' }[p.status] || p.status}</span>
                {p.starterKey && <span style={badgeSt(false)}>propuesta editable</span>}
              </Row>
              <div style={{ ...mutedSt, marginTop: 4 }}>
                {label(COACHING_MODALITIES, p.modality)} · foco {label(COACHING_FOCUS, p.focus).toLowerCase()}{p.specialties?.length ? ` · ${p.specialties.map(s => label(COACHING_SPECIALTIES, s)).join(', ')}` : ''}
                {' · '}{p.durationWeeks} semanas · {p.modality === 'team' ? `${p.groupSessions} grupales + ${p.individualSessions} individuales por persona` : `${p.sessions} sesiones`} · v{p.version}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── People ──

function PeopleTab({ people, memberships, processes, reload, focusId, onOpenProcess, onClearFocus = null, editId = null }) {
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [dupes, setDupes] = useState(null);
  // Arriving with a person (from the Directorio, a process or a contact): their card opens directly
  const [profileId, setProfileId] = useState(focusId || null);
  useEffect(() => { if (focusId) setProfileId(focusId); }, [focusId]);
  // Editing a card asked from the person's file: its form opens here (nothing changes until «Guardar»)
  useEffect(() => { const p = editId && people.find(x => x.id === editId); if (p) { setProfileId(null); setDupes(null); setForm(p); } }, [editId, people]);
  useEffect(() => { coachCall({ action: 'linkSuggestions' }).then(d => setSuggestions(d.suggestions)).catch(() => setSuggestions([])); }, [people]);
  useEffect(() => { if (focusId) document.getElementById(`person-${focusId}`)?.scrollIntoView?.({ block: 'center' }); }, [focusId]);
  const save = async (confirmNew = false) => {
    setError('');
    try { await coachCall({ action: 'savePerson', id: form.id, person: form, confirmNew }); setForm(null); setDupes(null); reload(); }
    catch (e) { if (e.code === 'person_exists') setDupes(e.data); else setError(e.message); }
  };
  const procName = (id) => processes.find(p => p.id === id)?.name || '—';
  // Only a card without participations; the server checks it again (also the diagnostics)
  const removePerson = async (p) => {
    if (!window.confirm(`¿Borrar la ficha de ${p.name}?\n\nSolo se puede si no participa en ningún proceso ni diagnóstico. El contacto comercial vinculado, si hay, no cambia.`)) return;
    setError('');
    try { await coachCall({ action: 'deletePerson', id: p.id, confirm: true }); reload(); } catch (e) { setError(e.message); }
  };
  if (profileId && people.some(x => x.id === profileId)) {
    return <PersonProfile personId={profileId} onBack={() => { setProfileId(null); onClearFocus?.(); }} onEdit={(p) => { setProfileId(null); onClearFocus?.(); setDupes(null); setForm(people.find(x => x.id === p.id) || p); }} onOpenProcess={(id, at) => onOpenProcess(id, at, profileId)} />;
  }
  return (
    <div style={cardSt}>
      <Row style={{ justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Personas</h3>
        <button className="action-btn btn-primary" onClick={() => { setDupes(null); setForm({ name: '', email: '', timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }); }}>+ Persona</button>
      </Row>
      {error && <div style={{ ...warnSt, padding: 10 }}>{error}</div>}
      <DuplicateReview key={people.length} onChanged={reload} />
      {form && (
        <div style={{ ...cardSt, background: 'rgba(30,42,44,0.02)' }}>
          {!form.id && (
            <details style={{ marginBottom: 10 }} open={!!form.stakeholderId}>
              <summary style={{ cursor: 'pointer', fontSize: 12 }}>{form.stakeholderId ? `Desde el contacto: ${form.contactName}` : 'Crear desde un contacto existente (opcional)'}</summary>
              <div style={{ marginTop: 6 }}>
                {form.stakeholderId
                  ? <button className="action-btn" style={{ fontSize: 11 }} onClick={() => setForm({ ...form, stakeholderId: null, contactName: '' })}>No vincular</button>
                  : <ContactPicker onPick={(c) => setForm({ ...form, stakeholderId: c.id, contactName: c.name, name: form.name || c.name, email: form.email || c.email || '' })} />}
                <div style={{ ...mutedSt, marginTop: 4 }}>Se vincula por el id del contacto, no por el nombre. El contacto comercial no ve nada de Acompañamiento.</div>
              </div>
            </details>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <Field label="Nombre"><input style={inputSt} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Email"><input style={inputSt} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></Field>
            <div style={{ gridColumn: '1 / -1' }}><span style={labelSt}>Zona horaria</span><TimeZoneSelect value={form.timeZone || ''} onChange={tz => setForm({ ...form, timeZone: tz })} /><span style={{ ...mutedSt, fontSize: 11 }}>La persona ve sus horarios en esta zona. Las sesiones ya agendadas no cambian de momento.</span></div>
          </div>
          {dupes && (
            <div style={{ ...warnSt, padding: 10, marginTop: 10 }}>
              <div style={{ fontSize: 12 }}>{dupes.reason === 'contact_linked' ? 'Ese contacto ya tiene una ficha:' : 'Ya hay una persona con ese email. ¿Es la misma? Usa su ficha:'}</div>
              {(dupes.matches || []).map(m => <div key={m.id} style={{ fontSize: 13, margin: '4px 0' }}><b>{m.name}</b> · {m.email} <button className="action-btn" style={{ fontSize: 11 }} onClick={() => { setForm(null); setDupes(null); document.getElementById(`person-${m.id}`)?.scrollIntoView?.({ block: 'center' }); }}>Es esta: no crear otra</button></div>)}
              {dupes.reason !== 'contact_linked' && <button className="action-btn" style={{ fontSize: 11 }} onClick={() => save(true)}>Es otra persona: crear otra ficha</button>}
            </div>
          )}
          <Row style={{ marginTop: 10 }}><button className="action-btn btn-primary" onClick={() => save(false)}>Guardar</button><button className="action-btn" onClick={() => { setForm(null); setDupes(null); }}>Cancelar</button></Row>
        </div>
      )}
      {!people.length ? <Empty>Todavía no hay personas.</Empty> : (
        <div style={{ display: 'grid', gap: 6 }}>
          {people.map(p => (
            <div key={p.id} id={`person-${p.id}`} style={{ display: 'grid', gap: 4, borderBottom: '1px solid var(--globant-border)', padding: '8px 0', fontSize: 13, ...(focusId === p.id ? { background: 'rgba(91,191,181,0.08)' } : {}) }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span><button type="button" onClick={() => { setReturn('Fichas de participantes', { page: 'coaching', section: 'people' }, ['client']); window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'client', personId: p.id } })); }} style={{ border: 0, background: 'none', padding: 0, font: 'inherit', fontWeight: 700, color: 'var(--globant-green)', cursor: 'pointer', textAlign: 'left' }}>{p.name}</button> <span style={mutedSt}>{p.email} · {!p.timeZone ? 'sin zona horaria' : timeZoneOption(p.timeZone)?.label || <span style={{ color: '#9A6212' }}>zona horaria inválida: corrígela</span>}</span></span>
                <span style={{ display: 'flex', gap: 6 }}>
                  <button className="action-btn" style={{ fontSize: 11 }} onClick={() => { setDupes(null); setForm(p); }}>Editar ficha</button>
                  {!memberships.some(m => m.personId === p.id) && <button className="action-btn btn-danger" style={{ fontSize: 11 }} onClick={() => removePerson(p)}>Borrar ficha</button>}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', fontSize: 12 }}>
                <span style={mutedSt}>Participaciones · Acompañamiento:</span>
                {memberships.filter(m => m.personId === p.id && m.status !== 'left').map(m => <button key={m.processId} className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={() => onOpenProcess(m.processId)}>{procName(m.processId)} ↗</button>)}
                {!memberships.some(m => m.personId === p.id && m.status !== 'left') && <span style={mutedSt}>ninguno</span>}
              </div>
              <PersonDiagnostics personId={p.id} />
              <PersonContact person={p} suggestions={suggestions.find(s => s.personId === p.id)} onChanged={reload} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const DIAG_STATUS = Object.fromEntries(DIAGNOSTIC_STATUSES.map(s => [s.key, s.label]));
/** The diagnostics this person took part in (basic data of each participation, never answers or evaluations). */
function PersonDiagnostics({ personId }) {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => { setOpen(o => !o); if (!list) adminCall({ action: 'personDiagnostics', personId }).then(d => setList(d.diagnostics)).catch(() => setList([])); };
  return (
    <div style={{ fontSize: 12 }}>
      <span style={mutedSt}>Participaciones · Diagnostic: </span>
      <button className="action-btn" style={{ fontSize: 11, padding: '2px 8px' }} onClick={load}>{open ? 'Ocultar' : 'Ver'}</button>
      {open && <div style={{ ...mutedSt, marginTop: 4, paddingLeft: 8, borderLeft: '2px solid var(--globant-border)' }}>
        {!list ? 'Cargando…' : !list.length ? 'Ninguno.' : list.map(x => <div key={x.participantId}>{x.diagnosticName} · {x.accountName || 'sin cuenta'} · {t(ROLES[x.role]?.label) || x.role} · {t(DIAG_STATUS[x.status]) || x.status}{x.isTest ? ' · prueba' : ''}</div>)}
        {list && <div style={{ marginTop: 2 }}>Solo datos básicos: las respuestas y evaluaciones quedan en cada diagnóstico.</div>}
      </div>}
    </div>
  );
}

// ── New process ──

function NewProcess({ programs, people: loaded, onCreated, onCancel }) {
  const [extra, setExtra] = useState([]);
  const people = [...loaded, ...extra.filter(x => !loaded.some(p => p.id === x.id))];
  const usable = programs.filter(p => p.status !== 'archived');
  const [form, setForm] = useState({ programId: usable[0]?.id || '', personIds: [], name: '', specialties: null, groupChannel: true, sponsorName: '', sponsorEmail: '', transcriptRetentionDays: TRANSCRIPT_DEFAULTS.retentionDays, startsOn: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const program = usable.find(p => p.id === form.programId);
  const team = program?.modality === 'team';
  const togglePerson = (id) => setForm(f => ({ ...f, personIds: f.personIds.includes(id) ? f.personIds.filter(x => x !== id) : team ? [...f.personIds, id] : [id] }));
  const specialties = form.specialties ?? program?.specialties ?? [];
  const create = async () => {
    setBusy(true); setError('');
    try { const v = await coachCall({ action: 'createProcess', ...form, specialties }); onCreated(v.process.id); } catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div style={cardSt}>
      <h3 style={{ marginTop: 0 }}>Nuevo proceso</h3>
      <p style={mutedSt}>Se crea una copia del programa para este proceso: luego la adaptas sin tocar la plantilla. El foco queda como propuesto hasta registrar el acuerdo de la primera sesión.</p>
      {error && <div style={{ ...warnSt, padding: 10 }}>{error}</div>}
      {!usable.length ? <Empty>Primero crea o carga un programa.</Empty> : (
        <div style={{ display: 'grid', gap: 12 }}>
          <Field label="Programa">
            <select style={inputSt} value={form.programId} onChange={e => setForm({ ...form, programId: e.target.value, personIds: [], specialties: null })}>
              {usable.map(p => <option key={p.id} value={p.id}>{p.name} · {label(COACHING_MODALITIES, p.modality)}</option>)}
            </select>
          </Field>
          <div>
            <span style={labelSt}>{team ? 'Integrantes del equipo' : 'Persona'}</span>
            <div style={{ maxWidth: 560, marginBottom: 8 }}>
              <PersonPicker api={coachingPeopleApi(people)} exclude={form.personIds} onPick={(card) => { setExtra(x => [...x, card]); togglePerson(card.id); }} />
            </div>
            {!people.length ? <Empty>Busca o crea la persona aquí arriba.</Empty> : (
              <Row>{people.map(p => (
                <label key={p.id} style={{ fontSize: 13 }}>
                  <input type={team ? 'checkbox' : 'radio'} name="person" checked={form.personIds.includes(p.id)} onChange={() => togglePerson(p.id)} /> {p.name}
                </label>
              ))}</Row>
            )}
          </div>
          <Field label="Nombre del proceso (opcional)"><input style={inputSt} value={form.name} placeholder={program?.name} onChange={e => setForm({ ...form, name: e.target.value })} /></Field>
          <MoreOptions title="Especialidades, equipo, transcripciones y fecha de inicio">
            <div>
              <span style={labelSt}>Especialidades (solo si el cliente las pidió)</span>
              <Row>{COACHING_SPECIALTIES.map(s => (
                <label key={s.key} style={{ fontSize: 13 }}><input type="checkbox" checked={specialties.includes(s.key)} onChange={() => setForm({ ...form, specialties: specialties.includes(s.key) ? specialties.filter(x => x !== s.key) : [...specialties, s.key] })} /> {s.label}</label>
              ))}</Row>
            </div>
            {team && <>
              <label style={{ fontSize: 13 }}><input type="checkbox" checked={form.groupChannel} onChange={e => setForm({ ...form, groupChannel: e.target.checked })} /> Canal de mensajes del equipo (separado de los mensajes privados)</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                <Field label="Quien contrata o lidera (nombre)"><input style={inputSt} value={form.sponsorName} onChange={e => setForm({ ...form, sponsorName: e.target.value })} /></Field>
                <Field label="Email de quien contrata"><input style={inputSt} value={form.sponsorEmail} onChange={e => setForm({ ...form, sponsorEmail: e.target.value })} /></Field>
              </div>
            </>}
            <Field label="Conservar transcripciones (días)" hint="0 = se borran al cerrar el proceso. Pasado el plazo se borran solas.">
              <input type="number" min={0} max={TRANSCRIPT_DEFAULTS.maxRetentionDays} style={{ ...inputSt, width: 120 }} value={form.transcriptRetentionDays} onChange={e => setForm({ ...form, transcriptRetentionDays: Number(e.target.value) })} />
            </Field>
            <Field label="Inicio"><input type="date" style={{ ...inputSt, width: 180 }} value={form.startsOn} onChange={e => setForm({ ...form, startsOn: e.target.value })} /></Field>
          </MoreOptions>
          <Row><button className="action-btn btn-primary" disabled={busy || !form.personIds.length} onClick={create}>Crear proceso</button><button className="action-btn" onClick={onCancel}>Cancelar</button></Row>
        </div>
      )}
    </div>
  );
}

// ── Agenda ──

function AgendaTab() {
  const [av, setAv] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    coachCall({ action: 'getAvailability' }).then(d => setAv(d.availability)).catch(e => setMsg(e.message));
    coachCall({ action: 'agenda' }).then(d => setSessions(d.sessions)).catch(() => {});
  }, []);
  if (!av) return <div style={cardSt}>{msg || 'Cargando…'}</div>;
  const setDay = (wd, ranges) => setAv({ ...av, weekly: { ...av.weekly, [wd]: ranges } });
  const save = async () => { setMsg(''); try { const d = await coachCall({ action: 'saveAvailability', availability: av }); setAv(d.availability); setMsg('Guardado.'); } catch (e) { setMsg(e.message); } };
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={cardSt}>
        <h3 style={{ marginTop: 0 }}>Disponibilidad para turnos</h3>
        <p style={mutedSt}>Los participantes reservan y reprograman sus sesiones individuales dentro de estos horarios. Nadie puede reservar un horario que se superpone con otra sesión.</p>
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ maxWidth: 520 }}><span style={labelSt}>Tu zona horaria</span><TimeZoneSelect value={av.timeZone} onChange={tz => setAv({ ...av, timeZone: tz })} /><span style={{ ...mutedSt, fontSize: 11 }}>Los horarios de abajo se leen en esta zona. Cambiarla no mueve las reservas ya hechas: quedan a la misma hora real.</span></div>
          {WEEKDAYS.map(d => (
            <Row key={d.key}>
              <span style={{ width: 90, fontSize: 13 }}>{d.label}</span>
              {(av.weekly[d.key] || []).map((r, i) => (
                <span key={i} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                  <input type="time" style={{ ...inputSt, width: 110 }} value={r[0]} onChange={e => setDay(d.key, av.weekly[d.key].map((x, j) => (j === i ? [e.target.value, x[1]] : x)))} />–
                  <input type="time" style={{ ...inputSt, width: 110 }} value={r[1]} onChange={e => setDay(d.key, av.weekly[d.key].map((x, j) => (j === i ? [x[0], e.target.value] : x)))} />
                  <button className="action-btn" onClick={() => setDay(d.key, av.weekly[d.key].filter((_, j) => j !== i))}>✕</button>
                </span>
              ))}
              <button className="action-btn" style={{ fontSize: 11 }} onClick={() => setDay(d.key, [...(av.weekly[d.key] || []), ['09:00', '13:00']])}>+ franja</button>
            </Row>
          ))}
          <MoreOptions title="Duración, anticipación, días bloqueados">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
              <Field label="Minutos por sesión"><input type="number" style={inputSt} value={av.sessionMinutes} onChange={e => setAv({ ...av, sessionMinutes: Number(e.target.value) })} /></Field>
              <Field label="Descanso entre sesiones (min)"><input type="number" style={inputSt} value={av.bufferMinutes} onChange={e => setAv({ ...av, bufferMinutes: Number(e.target.value) })} /></Field>
              <Field label="Anticipación mínima (horas)"><input type="number" style={inputSt} value={av.minNoticeHours} onChange={e => setAv({ ...av, minNoticeHours: Number(e.target.value) })} /></Field>
              <Field label="Reservas hasta (días)"><input type="number" style={inputSt} value={av.horizonDays} onChange={e => setAv({ ...av, horizonDays: Number(e.target.value) })} /></Field>
            </div>
            <Field label="Días bloqueados (AAAA-MM-DD, uno por línea)">
              <textarea style={textareaSt} value={(av.blocked || []).join('\n')} onChange={e => setAv({ ...av, blocked: e.target.value.split('\n').map(x => x.trim()).filter(Boolean) })} />
            </Field>
          </MoreOptions>
        </div>
        <Row style={{ marginTop: 12 }}><button className="action-btn btn-primary" onClick={save}>Guardar disponibilidad</button>{msg && <span style={mutedSt}>{msg}</span>}</Row>
      </div>
      <div style={cardSt}>
        <h3 style={{ marginTop: 0 }}>Próximas sesiones</h3>
        {!sessions.length ? <Empty>No hay sesiones agendadas.</Empty> : sessions.map(s => (
          <div key={s.id} style={{ fontSize: 13, borderBottom: '1px solid var(--globant-border)', padding: '6px 0' }}>
            <b>{fmtInTz(s.startsAt, av.timeZone, { dateStyle: 'medium', timeStyle: 'short' })}</b> · {s.processName} · {s.title} <span style={badgeSt(s.kind === 'group')}>{s.kind === 'group' ? 'grupal' : 'individual'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Messages (inbox) ──

function MessagesTab({ onOpen }) {
  const [threads, setThreads] = useState(null);
  const [error, setError] = useState('');
  const load = () => { setError(''); coachCall({ action: 'inbox' }).then(d => setThreads(d.threads)).catch(e => setError(e.message)); };
  useEffect(load, []);
  const unread = (threads || []).reduce((n, t) => n + t.unread, 0);
  return (
    <div style={cardSt}>
      <Row style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>Conversaciones</h3>
        {unread > 0 && <span style={badgeSt(true)}>{unread} sin leer</span>}
      </Row>
      <p style={mutedSt}>Cada conversación privada es entre tú y una persona. El canal del equipo está separado: lo ve todo el equipo.</p>
      {error ? <ErrorState message={error} onRetry={load} /> : !threads ? <LoadingState /> : !threads.length ? <Empty>Todavía no hay conversaciones. Se empiezan desde cada proceso, en Mensajes.</Empty> : (
        <div role="list" style={{ display: 'grid' }}>
          {threads.map(t => (
            <button role="listitem" key={`${t.processId}-${t.memberId || 'group'}`} onClick={() => onOpen(t.processId, { tab: 'messages', thread: t.memberId || 'group' })}
              style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) auto', gap: 4, width: '100%', textAlign: 'left', background: t.unread ? 'rgba(91,191,181,0.06)' : 'none', border: 'none', borderBottom: '1px solid var(--globant-border)', padding: '10px 6px', color: 'var(--globant-text)', cursor: 'pointer', fontSize: 13 }}>
              <span style={{ minWidth: 0 }}>
                {t.channel === 'group' ? <Visibility kind="group" label="Canal del equipo" /> : <Visibility kind="shared" label="Privado" />}{' '}
                <b>{t.channel === 'group' ? t.processName : t.personName}</b>
                {t.channel !== 'group' && <span style={mutedSt}> · {t.processName}</span>}
                <span style={{ ...mutedSt, display: 'block' }}>Último mensaje: {t.lastFrom} · {fmtInTz(t.lastAt, undefined, { dateStyle: 'medium', timeStyle: 'short' })} · {t.count} en total</span>
              </span>
              {t.unread > 0 ? <span style={{ ...badgeSt(true), alignSelf: 'center' }}>{t.unread} sin leer</span> : <span />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// What each part is, as the menu calls it (src/navigation.js › COACHING_PAGES)
const HEADINGS = {
  processes: ['Gestión de clientes', 'Acompañamientos', 'Procesos individuales y grupales: acuerdo, integrantes, sesiones, actividades, avances y mensajes de cada uno. Los programas, en «Programas».'],
  people: ['Gestión de clientes · Directorio', 'Fichas de participantes', 'Crear, editar y vincular la ficha de una persona con su contacto (por id), y revisar posibles duplicados. Cada ficha también se abre desde el Directorio.'],
  programs: ['Gestión de clientes · Acompañamientos', 'Programas', 'Las plantillas con las que se crean los acompañamientos. Cambiar un programa nunca cambia un proceso ya creado.'],
  learning: ['Gestión de clientes', 'Cursos', 'Cursos, asignaciones, entregas por revisar y reglas de Mi recorrido.'],
  agenda: ['Gestión de clientes', 'Agenda y sesiones', 'Las sesiones de todos tus acompañamientos. Cada una se abre en su proceso.'],
  work: ['Gestión de clientes', 'Actividades y entregas', 'Lo que falta hacer en los procesos, aparte de los trabajos entregados que esperan tu revisión.'],
  messages: ['Gestión de clientes', 'Mensajes', 'Las conversaciones de tus acompañamientos. Cada una se abre en su proceso.'],
};

// initialTab / onTabChange: the menu opens this screen on one of its parts (Acompañamiento, Clientes › Personas,
// Servicios › Cursos: src/navigation.js › COACHING_PAGES) and follows when its own tabs change; without them it works as before.
export default function CoachingHub({ initialTab = 'processes', onTabChange = null }) {
  const [tab, setTabState] = useState(initialTab);
  const setTab = (t) => { setTabState(t); if (onTabChange) onTabChange(t); };
  // A different entry of the menu: open that part (the screen stays mounted)
  useEffect(() => { if (initialTab && initialTab !== tab) { setTabState(initialTab); setOpenId(null); setCreating(false); } }, [initialTab]); // eslint-disable-line react-hooks/exhaustive-deps
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showScope, setShowScope] = useState(false);
  const [focusPerson, setFocusPerson] = useState(null);
  const [editPerson, setEditPerson] = useState(null); // «Editar ficha de participante» from a client's file
  const [openAt, setOpenAt] = useState(null); // { tab, thread, session } when arriving from a conversation, a notification or a session
  const [learningAt, setLearningAt] = useState(null); // { enrollmentId, lessonId }: one submission to review, opened directly
  // Arriving from a contact card (App.jsx keeps where to go until this section opens)
  useEffect(() => {
    const go = (p) => { if (!p) return; if (p.section && !p.processId) { setTab(p.section); setOpenId(null); setLearningAt(p.section === 'learning' ? p.at || null : null); if (!p.personId) setFocusPerson(null); setEditPerson(p.editPersonId || null); } if (p.processId) { setTab('processes'); setOpenAt(p.at || null); setOpenId(p.processId); } if (p.personId) { setTab('people'); setFocusPerson(p.personId); } };
    go(window.__oikePendingCoaching); window.__oikePendingCoaching = null;
    // Handled while mounted: the pending one is cleared too, so it is never replayed when this screen opens again
    const h = (e) => { go(e.detail); window.__oikePendingCoaching = null; };
    window.addEventListener('oike:openCoaching', h);
    return () => window.removeEventListener('oike:openCoaching', h);
  }, []);
  const load = () => coachCall({ action: 'overview' }).then(setData).catch(e => setError(e.message));
  useEffect(() => { coachCall({ action: 'status' }).then(setStatus).catch(e => setError(e.message)); load(); }, []);

  return (
    <div className="page-content" style={{ padding: 20, maxWidth: 1150 }}>
      <PageHeader icon="🌱" area={HEADINGS[tab]?.[0] || 'Operaciones · Servicios'} title={HEADINGS[tab]?.[1] || 'Acompañamientos'} subtitle={HEADINGS[tab]?.[2] || ''}
        actions={<>
          <NotificationCenter call={coachCall} onOpen={(n) => { setTab('processes'); setOpenAt({ tab: n.target === 'followup' ? 'followup' : n.target, thread: n.kind === 'message' ? (n.channel === 'group' ? 'group' : n.threadMemberId) : null }); setOpenId(n.processId); }} />
          <button className="action-btn" style={{ fontSize: 12 }} onClick={() => setShowScope(v => !v)} aria-expanded={showScope}>Alcance del acompañamiento</button>
        </>} />
      {showScope && <p style={{ ...mutedSt, marginTop: 8 }}>{SCOPE_NOTE}</p>}
      <div style={{ height: 12 }} />
      <StatusBanner status={status} />
      {error && <ErrorState message={error} onRetry={() => { setError(''); load(); }} />}
      {!data ? (!error && <LoadingState />) : <>
        {tab === 'processes' && openId && <ProcessDetail key={`${openId}-${openAt?.tab || ''}-${openAt?.thread || ''}-${openAt?.session || ''}`} processId={openId} initial={openAt} onBack={() => { setOpenId(null); setOpenAt(null); load(); }} people={data.people} />}
        {tab === 'processes' && !openId && creating && <NewProcess programs={data.programs} people={data.people} onCancel={() => setCreating(false)} onCreated={id => { setCreating(false); setOpenId(id); load(); }} />}
        {tab === 'processes' && !openId && !creating && (
          <div style={cardSt}>
            <Row style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Acompañamientos</h3>
              <Row>
                <button className="action-btn" onClick={() => { setTab('programs'); setCreating(false); }}>Programas</button>
                <button className="action-btn btn-primary" onClick={() => setCreating(true)}>+ Nuevo acompañamiento</button>
              </Row>
            </Row>
            {!data.processes.length ? <Empty>Todavía no hay acompañamientos.</Empty> : (
              <div style={{ display: 'grid', gap: 8 }}>
                {data.processes.map(p => (
                  <button key={p.id} onClick={() => setOpenId(p.id)} style={{ textAlign: 'left', background: 'rgba(30,42,44,0.03)', border: '1px solid var(--globant-border)', borderRadius: 8, padding: 10, color: 'var(--globant-text)', cursor: 'pointer' }}>
                    <Row>
                      <b style={{ fontSize: 13 }}>{p.name}</b>
                      <span style={badgeSt(p.status === 'active')}>{label(PROCESS_STATUSES, p.status)}</span>
                      {p.unread > 0 && <span style={badgeSt(true)}>✉ {p.unread}</span>}
                    </Row>
                    <div style={{ ...mutedSt, marginTop: 4 }}>
                      {label(COACHING_MODALITIES, p.modality)} · foco {label(COACHING_FOCUS, p.focus).toLowerCase()} {p.focusAgreed ? '(acordado)' : '(propuesto)'}
                      {p.specialties?.length ? ` · ${p.specialties.map(s => label(COACHING_SPECIALTIES, s)).join(', ')}` : ''} · {p.people} {p.people === 1 ? 'persona' : 'personas'}
                      {p.pendingQuestionnaires ? ` · ${p.pendingQuestionnaires} cuestionario(s) pendiente(s)` : ''}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {tab === 'people' && <PeopleTab people={data.people} memberships={data.memberships} processes={data.processes} reload={load} focusId={focusPerson} editId={editPerson} onClearFocus={() => { setFocusPerson(null); setEditPerson(null); }}
          onOpenProcess={(id, at, fromPersonId) => {
            const person = fromPersonId && data.people.find(x => x.id === fromPersonId);
            if (person) setReturn(`la ficha de ${person.name}`, { page: 'coaching', personId: person.id }, ['coaching']);
            setTab('processes'); setOpenAt(at || null); setOpenId(id);
          }} />}
        {tab === 'programs' && <>
          <div style={{ marginBottom: 10 }}><button className="action-btn" onClick={() => setTab('processes')}>← Acompañamientos</button></div>
          <ProgramsTab programs={data.programs} reload={load} />
        </>}
        {tab === 'work' && <ActivitiesAndDeliveries onOpenProcess={(id, at) => { setReturn('Actividades y entregas', { page: 'deliveries' }, ['coaching']); setTab('processes'); setOpenAt(at || null); setOpenId(id); }}
          onOpenSubmission={(r) => { setReturn('Actividades y entregas', { page: 'deliveries' }, ['courses']); setLearningAt({ enrollmentId: r.enrollmentId, lessonId: r.lessonId }); setTab('learning'); }} />}
        {tab === 'learning' && <LearningTab key={learningAt ? `${learningAt.enrollmentId}-${learningAt.lessonId || ''}` : 'learning'} people={data.people} processes={data.processes} memberships={data.memberships} initialEnrollment={learningAt} />}
        {tab === 'agenda' && <AgendaTab />}
        {tab === 'messages' && <MessagesTab onOpen={(id, at) => { setTab('processes'); setOpenAt(at || null); setOpenId(id); }} />}
      </>}
    </div>
  );
}
